import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Layers } from 'lucide-react';
import SourceCombobox from '../common/SourceCombobox';
import {
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
} from '../../utils/validationMessages';

export interface IBLevelSettingsData {
  currentIBLevel: string;
  maximumSubIBLevels: number;
  subIBCommissionShare: number;
  autoApproveSubIB: boolean;
}

interface IBLevelSettingsProps {
  data: IBLevelSettingsData;
  onChange: (data: IBLevelSettingsData) => void;
}

const ibLevelOptions = [
  { value: 'master-ib', label: 'Master IB' },
  { value: 'sub-ib-level-1', label: 'Sub IB Level 1' },
  { value: 'sub-ib-level-2', label: 'Sub IB Level 2' },
];

const IBLevelSettings = ({ data, onChange }: IBLevelSettingsProps) => {
  const handleChange = (field: keyof IBLevelSettingsData, value: any) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/10 rounded-lg">
            <Layers className="h-5 w-5 text-purple-500" />
          </div>
          <div>
            <CardTitle>IB Level Settings</CardTitle>
            <CardDescription>
              Manage tier levels and multi-level IB structure
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ib-level">Current IB Level</Label>
          <SourceCombobox
            id="ib-level"
            items={ibLevelOptions.map(option => option.label)}
            placeholder="Select IB level"
            value={
              ibLevelOptions.find(
                option => option.value === data.currentIBLevel
              )?.label || ''
            }
            onChange={value => {
              const option = ibLevelOptions.find(item => item.label === value);
              if (option) handleChange('currentIBLevel', option.value);
            }}
            emptyText="No IB levels found."
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="max-levels">Maximum Sub-IB Levels</Label>
          <Input
            id="max-levels"
            type="number"
            placeholder="Enter max levels"
            value={data.maximumSubIBLevels}
            onKeyDown={preventInvalidNumberInput}
            onPaste={preventInvalidNumberPaste}
            onChange={e =>
              handleChange(
                'maximumSubIBLevels',
                parseFloat(e.target.value) || 0
              )
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="commission-share">Sub-IB Commission Share (%)</Label>
          <Input
            id="commission-share"
            type="number"
            placeholder="Enter share percentage"
            value={data.subIBCommissionShare}
            onKeyDown={preventInvalidNumberInput}
            onPaste={preventInvalidNumberPaste}
            onChange={e =>
              handleChange(
                'subIBCommissionShare',
                parseFloat(e.target.value) || 0
              )
            }
          />
        </div>
        <div className="flex items-center gap-2 pt-2">
          <Checkbox
            id="auto-approve"
            checked={data.autoApproveSubIB}
            onChange={e => handleChange('autoApproveSubIB', e.target.checked)}
          />
          <Label htmlFor="auto-approve" className="text-sm">
            Auto-approve sub-IB applications
          </Label>
        </div>
      </CardContent>
    </Card>
  );
};

export default IBLevelSettings;
